import type { ApiClient, FieldSubject } from '@carbonwebmail/api-client'
import type { ContactFieldDto } from '@carbonwebmail/contracts'
import { useCallback } from 'react'
import { useLoaded } from './useLoaded.js'

/**
 * The fields this organisation declared, on whichever thing declares them.
 *
 * Several screens ask: each board draws one column per field, and the
 * new-contact form asks for every one of them. One loader, so a field added in
 * one place appears in the others for the same reason rather than by
 * coincidence.
 *
 * A failure leaves the caller with no fields rather than no screen. The board
 * still lists contacts with its fixed columns, and the form still takes an
 * address — worse screens, not broken ones.
 */
export function useDeclaredFields({
  client,
  subject,
}: {
  readonly client: ApiClient
  readonly subject: FieldSubject
}): {
  readonly fields: readonly ContactFieldDto[]
  /** Called after one is declared, so the new column appears without a refresh. */
  readonly reload: () => Promise<void>
} {
  const load = useCallback(() => client.declaredFields(subject), [client, subject])
  const { state, reload } = useLoaded(load, 'We could not load your fields.')

  return { fields: state.status === 'ready' ? state.value.fields : [], reload }
}
